import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Wallet, Banknote, Calculator, Shield, 
  AlertCircle, CheckCircle, Info, ChevronDown,
  Loader2, X, CreditCard, RefreshCw
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import MobileBottomNav from '../../components/layout/MobileBottomNav';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { getPayoutMethods, processWithdrawal, getWalletBalance, getFeePreview } from '../../services/api';

const WithdrawPage = () => {
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState('');
  const [displayAmount, setDisplayAmount] = useState('');
  const [description, setDescription] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [feeBreakdown, setFeeBreakdown] = useState(null);
  const [showFeeDetails, setShowFeeDetails] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [payoutMethods, setPayoutMethods] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [showBankDropdown, setShowBankDropdown] = useState(false);
  const [calculatingFees, setCalculatingFees] = useState(false);
  const [userReceives, setUserReceives] = useState(0);
  const [totalFees, setTotalFees] = useState(0);

  // Refs for focus management
  const amountInputRef = useRef(null);
  const descriptionInputRef = useRef(null);
  const mobileAmountInputRef = useRef(null);
  const mobileDescriptionInputRef = useRef(null);

  useEffect(() => {
    if (!authUser) {
      navigate('/login');
      return;
    }
    fetchInitialData();
  }, [authUser, navigate]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      
      // Fetch wallet balance
      const balanceRes = await getWalletBalance(authUser.user_id);
      if (balanceRes && balanceRes.success) {
        setWalletBalance(parseFloat(balanceRes.balance) || 0);
      }

      // Fetch payout methods
      const payoutRes = await getPayoutMethods();

      if (payoutRes && payoutRes.success) {
        const methods = (payoutRes.payout_methods || []).map(method => {
          let details = {};
          try {
            if (typeof method.details === 'string') {
              details = JSON.parse(method.details);
            } else if (method.details && typeof method.details === 'object') {
              details = method.details;
            }
          } catch (e) {
            // console.error('Error parsing method details:', e);
            details = {};
          }
          
          return {
            ...method,
            details: details
          };
        });
        
        setPayoutMethods(methods);
        
        if (methods.length > 0) {
          const defaultMethod = methods.find(m => m.is_default === 1 || m.is_default === true) || methods[0];
          setSelectedMethod(defaultMethod);
        }
      }
    } catch (error) {
      // console.error('Error fetching initial data:', error);
      setError('Failed to load wallet data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatNumberWithCommas = (value) => {
    if (!value) return '';
    
    const numericValue = value.replace(/[^0-9.]/g, '');
    if (!numericValue) return '';
    
    const parts = numericValue.split('.');
    let wholePart = parts[0];
    let decimalPart = parts[1] || '';
    
    if (decimalPart.length > 2) {
      decimalPart = decimalPart.substring(0, 2);
    }
    
    if (wholePart) {
      wholePart = wholePart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    
    return decimalPart ? `${wholePart}.${decimalPart}` : wholePart;
  };

  const handleAmountChange = (e, isMobile = false) => {
    const input = e.target.value;
    let cleaned = input.replace(/[₦,]/g, '');
    cleaned = cleaned.replace(/[^0-9.]/g, '');
    
    const decimalCount = (cleaned.match(/\./g) || []).length;
    if (decimalCount > 1) {
      const parts = cleaned.split('.');
      cleaned = parts[0] + '.' + parts.slice(1).join('');
    }
    
    setAmount(cleaned);
    
    if (cleaned) {
      const formatted = formatNumberWithCommas(cleaned);
      setDisplayAmount(`₦${formatted}`);
    } else {
      setDisplayAmount('');
    }
    
    setError('');
    setSuccess('');
    setFeeBreakdown(null);
    setUserReceives(0);
    setTotalFees(0);

    // Keep focus on input
    setTimeout(() => {
      if (isMobile) {
        mobileAmountInputRef.current?.focus();
      } else {
        amountInputRef.current?.focus();
      }
    }, 10);
  };

  const handleQuickAmount = async (quickAmount, isMobile = false) => {
    setAmount(quickAmount.toString());
    const formatted = formatNumberWithCommas(quickAmount.toString());
    setDisplayAmount(`₦${formatted}`);
    setError('');
    setSuccess('');
    
    // Focus back on amount input
    setTimeout(() => {
      if (isMobile) {
        mobileAmountInputRef.current?.focus();
      } else {
        amountInputRef.current?.focus();
      }
    }, 10);
    
    // Calculate fees for quick amount
    if (quickAmount >= 100) {
      await calculateFeesForAmount(quickAmount);
    } else {
      setFeeBreakdown(null);
      setUserReceives(0);
      setTotalFees(0);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '₦0.00';
    
    return `₦${parseFloat(amount).toLocaleString('en-NG', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
  };

  const calculateFeesForAmount = useCallback(async (withdrawalAmount) => {
    if (!withdrawalAmount || withdrawalAmount < 100) {
      setFeeBreakdown(null);
      setUserReceives(0);
      setTotalFees(0);
      return null;
    }
    
    try {
      setCalculatingFees(true);
      setError('');
      
      // Call backend API to get actual fee breakdown
      const feeData = await getFeePreview(withdrawalAmount, 'withdrawal');
      
      if (feeData.success) {
        setFeeBreakdown(feeData.fee_info);
        
        // Calculate user receives and total fees
        const totalFees = feeData.fee_info.total_fees || 0;
        const userReceives = feeData.fee_info.user_receives || (withdrawalAmount - totalFees);
        
        setTotalFees(totalFees);
        setUserReceives(userReceives);
        
        return feeData.fee_info;
      } else {
        throw new Error(feeData.error || 'Failed to calculate fees');
      }
      
    } catch (error) {
      // console.error('Error calculating fees:', error);
      setError('Unable to calculate fees. Please try again.');
      setFeeBreakdown(null);
      setUserReceives(0);
      setTotalFees(0);
      return null;
    } finally {
      setCalculatingFees(false);
    }
  }, []);

  // Debounced fee calculation
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (amount && parseFloat(amount) >= 100) {
        calculateFeesForAmount(parseFloat(amount));
      } else {
        setFeeBreakdown(null);
        setUserReceives(0);
        setTotalFees(0);
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [amount, calculateFeesForAmount]);

  const handleWithdraw = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    const withdrawalAmount = parseFloat(amount);
    
    if (withdrawalAmount > walletBalance) {
      setError('Insufficient balance');
      return;
    }
    
    if (withdrawalAmount < 100) {
      setError('Minimum withdrawal amount is ₦100');
      return;
    }

    if (!selectedMethod) {
      setError('Please select a payout method');
      return;
    }

    // Ensure we have fresh fee calculation
    const latestFees = await calculateFeesForAmount(withdrawalAmount);
    if (!latestFees) {
      setError('Unable to calculate fees. Please try again.');
      return;
    }

    if (withdrawalAmount > walletBalance) {
      setError('Insufficient balance for withdrawal');
      return;
    }

    try {
      setProcessing(true);
      setError('');
      setSuccess('');
      
      const confirmWithdrawal = window.confirm(
        `Confirm Withdrawal:\n\n` +
        `Amount to Debit: ${formatCurrency(withdrawalAmount)}\n` +
        `Total Fees: ${formatCurrency(totalFees)}\n` +
        `You Will Receive: ${formatCurrency(userReceives)}\n` +
        `Bank: ${selectedMethod.details.bank_name}\n` +
        `Account: ${selectedMethod.details.account_number}\n\n` +
        `OTP will be sent to your email.\n` +
        `Continue?`
      );

      if (!confirmWithdrawal) {
        setProcessing(false);
        return;
      }

      const result = await processWithdrawal(
        authUser.user_id,
        selectedMethod.payout_method_id,
        withdrawalAmount,
        description || 'Wallet Withdrawal'
      );

      if (result.success) {
        navigate('/wallet/withdraw-otp', {
          state: {
            transactionRef: result.transaction_ref,
            amount: withdrawalAmount,
            payoutMethod: selectedMethod
          }
        });
      } else {
        throw new Error(result.error || 'Withdrawal failed');
      }
    } catch (err) {
      // console.error('Withdrawal error:', err);
      
      if (err.message.includes('INVALID_OTP') || err.message.includes('WITHDRAWAL_FAILED')) {
        setError('Previous withdrawal failed. Please try again.');
      } else {
        setError('Withdrawal failed: ' + err.message);
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleBack = () => {
    navigate('/wallet');
  };

  const handleAddPayoutMethod = () => {
    navigate('/wallet/payout-methods');
  };

  const handleDescriptionChange = (e, isMobile = false) => {
    setDescription(e.target.value);
    // Keep focus
    setTimeout(() => {
      if (isMobile) {
        mobileDescriptionInputRef.current?.focus();
      } else {
        descriptionInputRef.current?.focus();
      }
    }, 10);
  };

  const handleRefreshFees = async () => {
    if (amount && parseFloat(amount) >= 100) {
      await calculateFeesForAmount(parseFloat(amount));
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <Loader2 className="w-8 h-8 text-orange-500 animate-spin mb-2" />
          <p className="text-gray-600">Loading wallet data...</p>
        </div>
      </div>
    );
  }

  // ========== MOBILE VIEW ==========
  const MobileView = () => (
    <div className="lg:hidden min-h-screen bg-gray-50 pb-20">
      {/* Mobile Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100"
            >
              <ArrowLeft className="w-5 h-5 text-black" />
            </button>
            
            <h1 className="text-lg font-bold text-black">Withdraw Funds</h1>
            
            <div className="w-10"></div>
          </div>
        </div>
      </header>

      <main className="px-4 py-4">
        {/* Balance Card */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <Wallet className="w-6 h-6 text-green-500" />
              <div>
                <p className="text-sm text-gray-600">Available Balance</p>
                <p className="text-xl font-bold text-black">{formatCurrency(walletBalance)}</p>
              </div>
            </div>
            <span className="text-xs text-gray-500">Min ₦100</span>
          </div>
        </div>

        {/* Amount Input */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Amount to Withdraw
            </label>
            {calculatingFees && (
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <Loader2 className="w-3 h-3 animate-spin" />
                Calculating fees...
              </div>
            )}
          </div>
          <div className="relative mb-3">
            <input
              ref={mobileAmountInputRef}
              type="text"
              value={displayAmount}
              onChange={(e) => handleAmountChange(e, true)}
              placeholder="₦0.00"
              className="w-full p-3 pl-12 text-xl font-bold border-2 border-gray-300 rounded-lg"
            />
            <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-xl font-bold text-gray-700">
              ₦
            </span>
          </div>
          
          {error && (
            <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              <span>{error}</span>
            </p>
          )}

          {/* Quick Amounts */}
          <div className="mt-4">
            <p className="text-sm text-gray-600 mb-2">Quick Select:</p>
            <div className="grid grid-cols-4 gap-2">
              {[500, 1000, 5000, 10000].map((quickAmount) => {
                if (walletBalance >= quickAmount) {
                  return (
                    <button
                      key={quickAmount}
                      onClick={() => handleQuickAmount(quickAmount, true)}
                      className="p-2 border rounded-lg text-sm hover:border-[#ff660e] hover:bg-orange-50"
                    >
                      ₦{quickAmount.toLocaleString()}
                    </button>
                  );
                }
                return null;
              })}
            </div>
            
            {walletBalance >= 100 && (
              <button
                onClick={() => handleQuickAmount(walletBalance, true)}
                className="w-full mt-3 p-3 border-2 border-[#ff660e] rounded-lg text-center bg-orange-50 hover:bg-orange-100"
              >
                <span className="font-bold text-[#ff660e]">Withdraw All</span>
              </button>
            )}
          </div>
        </div>

        {/* Fee Summary (Simplified) */}
        {feeBreakdown && totalFees > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Calculator className="w-4 h-4 text-[#ff660e]" />
                <p className="font-medium text-black">Fee Summary</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleRefreshFees}
                  disabled={calculatingFees}
                  className="p-1 hover:bg-gray-100 rounded"
                  title="Refresh fees"
                >
                  <RefreshCw className={`w-4 h-4 ${calculatingFees ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Requested Amount:</span>
                <span>{formatCurrency(amount)}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-600">Total Fees:</span>
                <span className="text-red-600">-{formatCurrency(totalFees)}</span>
              </div>
              
              <div className="border-t pt-2">
                <div className="flex justify-between font-semibold">
                  <span className="text-green-700">You Receive:</span>
                  <span className="text-green-700">{formatCurrency(userReceives)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Description */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description (Optional)
          </label>
          <input
            ref={mobileDescriptionInputRef}
            type="text"
            value={description}
            onChange={(e) => handleDescriptionChange(e, true)}
            placeholder="e.g., Salary, Savings"
            className="w-full p-3 border rounded-lg"
          />
        </div>

        {/* Payout Method */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Payout Method
          </label>
          
          {payoutMethods.length === 0 ? (
            <div className="text-center p-4 border-2 border-dashed rounded-lg">
              <CreditCard className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-600 text-sm mb-3">No bank account added</p>
              <button
                onClick={handleAddPayoutMethod}
                className="text-sm text-[#ff660e] font-medium"
              >
                + Add Bank Account
              </button>
            </div>
          ) : (
            <div className="relative">
              <button
                onClick={() => setShowBankDropdown(!showBankDropdown)}
                className="w-full p-3 border rounded-lg text-left"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{selectedMethod?.details?.bank_name || 'Select Bank'}</p>
                    <p className="text-sm text-gray-600">
                      {selectedMethod?.details?.account_number ? `••••${selectedMethod.details.account_number.slice(-4)}` : ''}
                    </p>
                  </div>
                  <ChevronDown className={`w-5 h-5 ${showBankDropdown ? 'rotate-180' : ''}`} />
                </div>
              </button>
              
              {showBankDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg">
                  {payoutMethods.map((method) => (
                    <button
                      key={method.payout_method_id}
                      onClick={() => {
                        setSelectedMethod(method);
                        setShowBankDropdown(false);
                      }}
                      className="w-full p-3 border-b hover:bg-gray-50 text-left last:border-0"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{method.details?.bank_name}</p>
                          <p className="text-sm text-gray-600">••••{method.details?.account_number?.slice(-4)}</p>
                        </div>
                        {method.is_default === 1 && (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                            Default
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Status Messages */}
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <p className="text-sm text-green-700">{success}</p>
            </div>
          </div>
        )}

        {/* Withdraw Button */}
        <button
          onClick={handleWithdraw}
          disabled={!amount || parseFloat(amount) < 100 || !selectedMethod || processing || calculatingFees}
          className="w-full bg-gradient-to-r from-[#ff660e] to-orange-500 text-white py-3.5 rounded-lg font-bold disabled:opacity-50 mb-4"
        >
          {processing ? 'Processing...' : calculatingFees ? 'Calculating...' : 'Withdraw Funds'}
        </button>

        {/* Info */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center gap-2 mb-3">
            <Info className="w-5 h-5 text-[#ff660e]" />
            <h3 className="font-bold text-black">Important Info</h3>
          </div>
          <div className="space-y-2 text-sm text-gray-600">
            <p>• Minimum withdrawal: ₦100</p>
            <p>• Processing: 1-24 hours</p>
            <p>• OTP will be sent to email</p>
            <p>• Total fees shown above</p>
          </div>
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );

  // ========== DESKTOP VIEW ==========
  const DesktopView = () => (
    <div className="hidden lg:block min-h-screen bg-gray-50">
      {/* Desktop Header */}
      <Header />
      
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Withdraw Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              {/* Header */}
              <div className="flex items-center gap-4 mb-6">
                <button
                  onClick={handleBack}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <ArrowLeft className="w-5 h-5 text-black" />
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-black">Withdraw Funds</h1>
                  <p className="text-gray-600">Transfer money to your bank account</p>
                </div>
              </div>

              {/* Balance Display */}
              <div className="mb-6 p-5 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Wallet className="w-8 h-8 text-green-600" />
                    <div>
                      <p className="text-sm text-gray-600">Available Balance</p>
                      <p className="text-2xl font-bold text-black">{formatCurrency(walletBalance)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Minimum: ₦100</p>
                  </div>
                </div>
              </div>

              {/* Amount Input */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Amount to Withdraw (₦)
                  </label>
                  {calculatingFees && (
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Calculating fees...
                    </div>
                  )}
                </div>
                <div className="relative">
                  <input
                    ref={amountInputRef}
                    type="text"
                    value={displayAmount}
                    onChange={handleAmountChange}
                    placeholder="₦0.00"
                    className="w-full p-4 pl-14 text-2xl font-bold border-2 border-gray-300 rounded-xl"
                  />
                  <span className="absolute left-6 top-1/2 transform -translate-y-1/2 text-2xl font-bold text-gray-700">
                    ₦
                  </span>
                </div>
                {error && (
                  <p className="mt-2 text-sm text-red-600 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    <span>{error}</span>
                  </p>
                )}
              </div>

              {/* Quick Amounts */}
              <div className="mb-6">
                <p className="text-sm text-gray-600 mb-3">Quick Select:</p>
                <div className="grid grid-cols-4 gap-3">
                  {[500, 1000, 5000, 10000].map((quickAmount) => {
                    if (walletBalance >= quickAmount) {
                      return (
                        <button
                          key={quickAmount}
                          onClick={() => handleQuickAmount(quickAmount)}
                          className="p-3 border rounded-xl hover:border-[#ff660e] hover:bg-orange-50 transition-colors"
                        >
                          <span className="font-medium">₦{quickAmount.toLocaleString()}</span>
                        </button>
                      );
                    }
                    return null;
                  })}
                </div>
                
                {walletBalance >= 100 && (
                  <button
                    onClick={() => handleQuickAmount(walletBalance)}
                    className="w-full mt-3 p-4 border-2 border-[#ff660e] rounded-xl text-center bg-orange-50 hover:bg-orange-100 transition-colors"
                  >
                    <div className="flex items-center justify-center gap-2">
                      <Banknote className="w-5 h-5 text-[#ff660e]" />
                      <span className="font-bold text-[#ff660e]">Withdraw All Funds</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {formatCurrency(walletBalance)} available
                    </p>
                  </button>
                )}
              </div>

              {/* Fee Summary (Simplified) */}
              {feeBreakdown && totalFees > 0 && (
                <div className="mb-6 bg-gray-50 border rounded-xl p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Calculator className="w-5 h-5 text-[#ff660e]" />
                      <h4 className="font-semibold text-black">Fee Summary</h4>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleRefreshFees}
                        disabled={calculatingFees}
                        className="p-1 hover:bg-gray-200 rounded"
                        title="Refresh fees"
                      >
                        <RefreshCw className={`w-4 h-4 ${calculatingFees ? 'animate-spin' : ''}`} />
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Requested Amount:</span>
                      <span className="font-medium">{formatCurrency(amount)}</span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Fees:</span>
                      <span className="font-medium text-red-600">
                        -{formatCurrency(totalFees)}
                      </span>
                    </div>
                    
                    <div className="border-t border-gray-300 pt-3">
                      <div className="flex justify-between">
                        <span className="font-bold text-green-700">You Will Receive:</span>
                        <span className="font-bold text-green-700 text-lg">
                          {formatCurrency(userReceives)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Description */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description (Optional)
                </label>
                <input
                  ref={descriptionInputRef}
                  type="text"
                  value={description}
                  onChange={handleDescriptionChange}
                  placeholder="e.g., Salary, Savings, Emergency funds"
                  className="w-full p-3 border rounded-xl"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Appears in your transaction history
                </p>
              </div>

              {/* Payout Method */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payout Method
                </label>
                
                {payoutMethods.length === 0 ? (
                  <div className="p-6 border-2 border-dashed rounded-xl text-center">
                    <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 mb-4">No bank account added yet</p>
                    <button
                      onClick={handleAddPayoutMethod}
                      className="px-4 py-2 bg-[#ff660e] text-white rounded-lg font-medium"
                    >
                      Add Bank Account
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <button
                      onClick={() => setShowBankDropdown(!showBankDropdown)}
                      className="w-full p-4 border rounded-xl text-left hover:border-[#ff660e] transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-gray-100 rounded-lg">
                            <CreditCard className="w-5 h-5 text-[#ff660e]" />
                          </div>
                          <div>
                            <p className="font-semibold">{selectedMethod?.details?.bank_name || 'Select Bank'}</p>
                            <p className="text-sm text-gray-600">{selectedMethod?.details?.account_number}</p>
                          </div>
                        </div>
                        <ChevronDown className={`w-5 h-5 ${showBankDropdown ? 'rotate-180' : ''}`} />
                      </div>
                    </button>
                    
                    {showBankDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-white border rounded-xl shadow-lg">
                        {payoutMethods.map((method) => (
                          <button
                            key={method.payout_method_id}
                            onClick={() => {
                              setSelectedMethod(method);
                              setShowBankDropdown(false);
                            }}
                            className="w-full p-4 border-b hover:bg-gray-50 text-left last:border-0"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">{method.details?.bank_name}</p>
                                <p className="text-sm text-gray-600">{method.details?.account_number}</p>
                                <p className="text-xs text-gray-500">{method.details?.account_name}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                {method.is_verified === 1 && (
                                  <Shield className="w-4 h-4 text-green-500" />
                                )}
                                {method.is_default === 1 && (
                                  <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
                                    Default
                                  </span>
                                )}
                              </div>
                            </div>
                          </button>
                        ))}
                        <div className="p-3 border-t">
                          <button
                            onClick={handleAddPayoutMethod}
                            className="w-full text-center text-[#ff660e] hover:text-[#ff5500] font-medium"
                          >
                            + Add New Bank Account
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Status Messages */}
              {success && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-medium text-green-800">Success!</p>
                      <p className="text-sm text-green-700">{success}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Withdraw Button */}
              <button
                onClick={handleWithdraw}
                disabled={!amount || parseFloat(amount) < 100 || !selectedMethod || processing || calculatingFees}
                className="w-full bg-gradient-to-r from-[#ff660e] to-orange-500 text-white py-4 rounded-xl font-bold text-lg hover:from-[#ff5500] hover:to-orange-600 disabled:opacity-50 transition-colors"
              >
                {processing ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processing...
                  </div>
                ) : calculatingFees ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Calculating fees...
                  </div>
                ) : (
                  'Withdraw Funds'
                )}
              </button>

              {/* Security Note */}
              <div className="mt-6 pt-6 border-t">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-green-600" />
                  <p className="text-gray-600">
                    <span className="font-medium">Secure Withdrawal:</span> OTP sent to email for verification
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Info */}
          <div className="space-y-6">
            {/* How it Works */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <Info className="w-5 h-5 text-[#ff660e]" />
                <h3 className="font-bold text-black">How It Works</h3>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <span className="font-bold text-blue-600">1</span>
                  </div>
                  <div>
                    <p className="font-medium text-black">Enter Amount</p>
                    <p className="text-sm text-gray-600">Minimum ₦100</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <span className="font-bold text-blue-600">2</span>
                  </div>
                  <div>
                    <p className="font-medium text-black">See Fee Summary</p>
                    <p className="text-sm text-gray-600">Total fees calculated instantly</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <span className="font-bold text-blue-600">3</span>
                  </div>
                  <div>
                    <p className="font-medium text-black">Confirm & Verify</p>
                    <p className="text-sm text-gray-600">Review and confirm with OTP</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <span className="font-bold text-blue-600">4</span>
                  </div>
                  <div>
                    <p className="font-medium text-black">Receive Funds</p>
                    <p className="text-sm text-gray-600">1-24 hours processing time</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Fee Information */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <Calculator className="w-5 h-5 text-[#ff660e]" />
                <h3 className="font-bold text-black">Fee Information</h3>
              </div>
              
              <div className="space-y-3">
                <div>
                  <p className="font-medium text-black">Total Fees Shown</p>
                  <p className="text-sm text-gray-600">See total fees before confirming</p>
                </div>
                
                <div>
                  <p className="font-medium text-black">Net Amount</p>
                  <p className="text-sm text-gray-600">Amount you'll receive after fees</p>
                </div>
                
                <div>
                  <p className="font-medium text-black">Processing Time</p>
                  <p className="text-sm text-gray-600">1-24 hours after verification</p>
                </div>
                
                <div>
                  <p className="font-medium text-black">Minimum Amount</p>
                  <p className="text-sm text-gray-600">₦100</p>
                </div>
              </div>
            </div>

            {/* Support */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h4 className="font-bold text-black mb-3">Need Help?</h4>
              <p className="text-gray-600 mb-4">Having issues with withdrawal?</p>
              <button
                onClick={() => navigate('/support')}
                className="text-[#ff660e] hover:text-[#ff5500] font-medium underline"
              >
                Contact Support
              </button>
            </div>
          </div>
        </div>
      </main>
      
      {/* Desktop Footer */}
      <Footer />
    </div>
  );

  return (
    <>
      {/* Render Mobile or Desktop View */}
      <MobileView />
      <DesktopView />
    </>
  );
};

export default WithdrawPage;